import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Building2, GraduationCap, Phone, Quote, Star } from "lucide-react";
import api from "@/lib/api";
import AdvisorCallbackForm from "@/components/AdvisorCallbackForm";
import GoogleReviews from "@/components/GoogleReviews";
import TestimonialVideos from "@/components/TestimonialVideos";
import SuccessStories from "@/components/SuccessStories";

const HERO_IMG = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CTA_IMG = "https://images.unsplash.com/photo-1501504905252-473c47e087f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

const decode = (s = "") => s
  .replace(/&nbsp;/g, " ")
  .replace(/&#x27;|&#39;/g, "'")
  .replace(/&quot;/g, '"')
  .replace(/&amp;/g, "&")
  .replace(/&lt;/g, "<")
  .replace(/&gt;/g, ">");

const plain = (html = "") => decode(html.replace(/<[^>]+>/g, " ")).replace(/[\u200b\u200c\u200d]/g, "").replace(/\s+/g, " ").trim();

const CATEGORIES = [
  ["all", "All"],
  ["data", "Data & AI"],
  ["cloud", "Cloud & DevOps"],
  ["security", "Networking & Security"],
  ["web", "Web Development"],
];

function categoryOf(t) {
  const s = `${t.program_completed || ""} ${t.role || ""}`.toLowerCase();
  if (/data|power bi|analytics|ai|machine/.test(s)) return "data";
  if (/aws|devops|cloud/.test(s)) return "cloud";
  if (/network|security|system/.test(s)) return "security";
  if (/full stack|web|software/.test(s)) return "web";
  return "all";
}

function Stars({ n = 5, testid }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${n} out of 5 stars`} data-testid={testid}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < n ? "fill-[#FFC72C] text-[#FFC72C]" : "fill-slate-200 text-slate-200"}`} />
      ))}
    </div>
  );
}

function ReviewAvatar({ item, testid }) {
  const [error, setError] = useState(false);
  const initials = (item.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (!item.photo || error) return <span className="w-14 h-14 rounded-full bg-[#17263B] text-[#FFC72C] flex items-center justify-center font-extrabold" data-testid={testid}>{initials}</span>;
  return <img src={item.photo} alt={item.name} onError={() => setError(true)} loading="lazy" className="w-14 h-14 rounded-full object-cover" data-testid={testid} />;
}

function StudentReviews({ items }) {
  const [filter, setFilter] = useState("all");
  const visible = items.filter((t) => filter === "all" || categoryOf(t) === filter);
  const available = new Set(items.map(categoryOf));
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18" data-testid="student-reviews">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-700">
            <span className="w-7 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Student Reviews
          </span>
          <h2 className="m-0 mt-3 text-3xl sm:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight text-[#17263B]" data-testid="student-reviews-heading">
            What Our Students <span className="text-[#FFC72C]">Say</span>
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="student-review-filters">
          {CATEGORIES.filter(([key]) => key === "all" || available.has(key)).map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} className={`min-h-[40px] px-4 rounded-full text-sm font-extrabold transition-all ${filter === key ? "bg-[#FFC72C] text-[#17263B] shadow" : "bg-white border border-slate-200 text-slate-500 hover:border-[#FFC72C]"}`} data-testid={`student-review-filter-${key}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="mt-9 grid md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="student-review-grid">
        {visible.map((t, i) => (
          <figure key={`${t.name}-${i}`} className="m-0 rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_16px_40px_-28px_rgba(23,38,59,0.3)] hover:-translate-y-1 hover:border-[#FFC72C] transition-all flex flex-col" data-testid={`student-review-card-${i}`}>
            <Quote className="w-8 h-8 text-[#FFC72C] fill-[#FFC72C]" aria-hidden="true" />
            <blockquote className="m-0 mt-4 text-[15px] leading-relaxed text-slate-600 flex-1" data-testid={`student-review-quote-${i}`}>“{t.quote}”</blockquote>
            <figcaption className="mt-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <ReviewAvatar item={t} testid={`student-review-avatar-${i}`} />
                <div className="min-w-0">
                  <p className="m-0 text-sm font-extrabold text-[#17263B] truncate" data-testid={`student-review-name-${i}`}>{t.name}</p>
                  <Stars n={t.rating || 5} testid={`student-review-stars-${i}`} />
                  <p className="m-0 mt-0.5 text-xs text-slate-500 truncate">{[t.role, t.company].filter(Boolean).join(" · ")}</p>
                </div>
              </div>
              <span className="text-sm font-extrabold text-slate-700 shrink-0">{t.company}</span>
            </figcaption>
            <div className="mt-5 flex flex-wrap gap-2">
              {t.program_completed && <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600" data-testid={`student-review-program-${i}`}>{t.program_completed}</span>}
              <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-xs font-extrabold text-emerald-700">Placed</span>
            </div>
          </figure>
        ))}
      </div>
    </section>
  );
}

export default function TestimonialsPage({ lp }) {
  const [cms, setCms] = useState(null);
  const raw = lp.body_html || lp.overview_html || "";

  useEffect(() => {
    api.get("/website/sections/home_content").then((r) => setCms(r.data)).catch(() => setCms({ testimonials: [], placement_logos: [] }));
  }, []);

  const content = useMemo(() => {
    const blocks = [...raw.matchAll(/<(h2|h6|p|a)\b([^>]*)>([\s\S]*?)<\/\1>/gi)].map((m) => {
      const attrs = m[2] || "";
      return { tag: m[1].toLowerCase(), html: m[3], text: plain(m[3]), href: attrs.match(/href=["']([^"']+)/i)?.[1] || "" };
    });
    const pick = (needle) => blocks.find((b) => b.text.includes(needle));
    const leadMatch = raw.match(/<h2>CONTACT<\/h2>\s*([\s\S]*?)\s*<p>/i);
    const h6s = [...raw.matchAll(/<h6\b[^>]*>([\s\S]*?)<\/h6>/gi)].map((m) => ({ tag: "h6", html: m[1], text: plain(m[1]) }));
    return {
      studentsHeading: pick("Students and Professionals"),
      studentsIntro: pick("Learn why students"),
      storiesLink: pick("Explore Sucess Stories"),
      videoHeading: pick("Success Speaks Louder"),
      videoIntro: pick("Watch our real video testimonials"),
      videoLink: pick("Watch More!"),
      googleHeading: pick("What our Alumni"),
      googleIntro: pick("Discover real success stories"),
      googleLink: pick("Read More!"),
      contactHeading: pick("CONTACT"),
      contactLead: leadMatch ? plain(leadMatch[1]) : "Feel free to say HELLO! we'd love to help!",
      contactFormText: pick("Fill out this form"),
      callText: h6s.find((b) => b.text.includes("In a hurry?")),
      phoneText: h6s.find((b) => b.text.includes("+91-9015")),
      telHref: blocks.find((b) => b.tag === "a" && b.href.startsWith("tel:"))?.href || "tel:+919015887887",
      contactImg: raw.match(/<img[^>]+src=["']([^"']+)/i)?.[1] || "",
    };
  }, [raw]);

  const stories = (cms?.testimonials || []).filter((t) => t.visible !== false && t.name);
  const videoHeader = (
    <div>
      <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.22em] text-amber-700">
        <span className="w-7 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Video Testimonials
      </span>
      {content.videoHeading && <h2 className="m-0 mt-3 text-3xl sm:text-4xl lg:text-[2.8rem] font-extrabold tracking-tight text-[#17263B]" dangerouslySetInnerHTML={{ __html: content.videoHeading.html }} data-testid="video-testimonials-heading" />}
      {content.videoIntro && <p className="m-0 mt-3 text-[15px] leading-relaxed text-slate-500 max-w-2xl" dangerouslySetInnerHTML={{ __html: content.videoIntro.html }} data-testid="video-testimonials-intro" />}
    </div>
  );

  return (
    <div className="bg-white text-slate-900" data-testid="testimonials-page">
      <section className="relative overflow-hidden" data-testid="testimonials-hero">
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#FFC72C]/30 rounded-full blur-3xl" aria-hidden="true" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-[0.95fr_1.05fr] gap-10 lg:gap-14 items-center relative">
          <div className="min-w-0">
            <h1 className="m-0 mt-4 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.03] text-[#17263B]" data-testid="testimonials-title">
              {lp.h1 || lp.title}
            </h1>
            <p className="m-0 mt-2 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.03]" data-testid="testimonials-hero-mockline">
              <span className="text-[#17263B]">Real Learning. </span><span className="text-[#FFC72C]">Real Experiences.</span><br />
              <span className="text-[#17263B]">Real Career Growth.</span>
            </p>
            {content.studentsHeading && <h2 className="m-0 mt-5 text-[11px] font-extrabold uppercase tracking-[0.24em] text-slate-500 [&_strong]:font-extrabold" dangerouslySetInnerHTML={{ __html: content.studentsHeading.html }} data-testid="testimonials-cms-eyebrow" />}
            {content.studentsIntro && <p className="m-0 mt-5 text-base sm:text-lg leading-relaxed text-slate-600 max-w-xl" dangerouslySetInnerHTML={{ __html: content.studentsIntro.html }} data-testid="testimonials-intro" />}
            <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl" data-testid="testimonials-stats">
              {[[GraduationCap, "10,000+", "Students Trained"], [Briefcase, "2,000+", "Students Placed"], [Building2, "500+", "Hiring Partners"]].map(([Icon, value, label], i) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid={`testimonials-stat-${i}`}>
                  <span className="w-10 h-10 rounded-xl bg-[#FFC72C] flex items-center justify-center"><Icon className="w-5 h-5 text-[#17263B]" /></span>
                  <p className="m-0 mt-3 text-lg sm:text-2xl font-extrabold text-[#17263B]">{value}</p>
                  <p className="m-0 text-[11px] sm:text-xs font-semibold text-slate-500">{label}</p>
                </div>
              ))}
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              {content.storiesLink && <a href={content.storiesLink.href || "#alumni"} className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] text-[#17263B] font-extrabold shadow-[0_10px_24px_rgba(255,199,44,.32)] transition-all" data-testid="testimonials-stories-cta">{content.storiesLink.text}<ArrowRight className="w-[18px] h-[18px]" /></a>}
              <a href="#testimonials-contact" className="inline-flex items-center gap-2 min-h-[52px] px-6 rounded-full border-[1.5px] border-slate-200 bg-white hover:border-[#FFC72C] text-[#17263B] font-extrabold transition-colors" data-testid="testimonials-advisor-cta">Talk to an Advisor</a>
            </div>
          </div>
          <div className="relative min-w-0">
            <div className="absolute -left-5 top-8 w-24 h-36 bg-[#FFC72C] rounded-l-3xl" aria-hidden="true" />
            <img src={HERO_IMG} alt="Training Basket learner working on a laptop" loading="eager" decoding="async" className="relative w-full aspect-[4/3] object-cover rounded-[2rem] shadow-[0_28px_60px_-24px_rgba(23,38,59,0.38)]" data-testid="testimonials-hero-image" />
            <div className="absolute right-5 top-5 -rotate-3 text-3xl leading-tight text-[#FFC72C] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="testimonials-hero-script">From Learning<br />to a Better<br />Career</div>
          </div>
        </div>
      </section>

      {cms && <TestimonialVideos cms={cms} layout="rail" header={videoHeader} moreLink={content.videoLink} />}
      {cms && <StudentReviews items={stories} />}
      <GoogleReviews headingHtml={content.googleHeading?.html} introHtml={content.googleIntro?.html} moreLink={content.googleLink} />
      {cms && <div id="alumni" className="scroll-mt-24"><SuccessStories cms={cms} /></div>}

      <section id="testimonials-contact" className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-18 scroll-mt-24" data-testid="testimonials-contact">
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-8 items-start rounded-[2rem] border border-slate-200 bg-white p-5 sm:p-8 shadow-[0_20px_50px_-28px_rgba(23,38,59,0.28)]">
          <div>
            {content.contactHeading && <h2 className="m-0 text-3xl sm:text-4xl font-extrabold tracking-tight text-[#17263B]" dangerouslySetInnerHTML={{ __html: content.contactHeading.html }} data-testid="testimonials-contact-heading" />}
            <p className="m-0 mt-3 text-[15px] leading-relaxed text-slate-600" data-testid="testimonials-contact-lead">{content.contactLead}</p>
            {content.contactFormText && <p className="m-0 mt-2 text-[15px] leading-relaxed text-slate-500" dangerouslySetInnerHTML={{ __html: content.contactFormText.html }} data-testid="testimonials-contact-form-text" />}
            <a href={content.telHref} className="mt-6 inline-flex items-center gap-3 text-[#17263B] font-extrabold hover:text-amber-700 transition-colors" data-testid="testimonials-contact-call">
              {content.contactImg ? <img src={content.contactImg} alt="" className="w-8 h-8" loading="lazy" /> : <span className="w-11 h-11 rounded-full bg-[#FFC72C] flex items-center justify-center"><Phone className="w-5 h-5" /></span>}
              <span>
                {content.callText && <h6 className="m-0 text-sm font-extrabold" dangerouslySetInnerHTML={{ __html: content.callText.html }} />}
                {content.phoneText && <h6 className="m-0 mt-0.5 text-sm font-extrabold" dangerouslySetInnerHTML={{ __html: content.phoneText.html }} />}
              </span>
            </a>
          </div>
          <AdvisorCallbackForm path={lp.url_path} testidPrefix="testimonials-advisor" submitLabel="Request a call back" />
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20" data-testid="testimonials-final-cta">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#0F1115] text-white grid lg:grid-cols-[1.15fr_0.85fr] min-h-[320px]">
          <div className="p-7 sm:p-10 lg:p-12 flex flex-col justify-center relative z-10">
            <span className="inline-flex items-center gap-2.5 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#FFC72C]">
              <span className="w-8 h-[3px] rounded-full bg-[#FFC72C]" aria-hidden="true" /> Your Success Story Could Be Next
            </span>
            <h2 className="m-0 mt-4 text-3xl sm:text-4xl lg:text-[2.7rem] font-extrabold tracking-tight leading-tight" data-testid="testimonials-final-title">
              Become Job Ready <span className="text-[#FFC72C]">Like Our Students</span>
            </h2>
            <p className="m-0 mt-4 text-[15px] leading-relaxed text-slate-300 max-w-xl" data-testid="testimonials-final-text">
              Join our industry-focused training programs and take the next step towards a successful career.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/courses" className="inline-flex items-center gap-2 min-h-[50px] px-6 rounded-full bg-[#FFC72C] hover:bg-[#F5B800] active:scale-[.98] text-[#17263B] font-extrabold transition-all" data-testid="testimonials-final-courses">Explore Courses <ArrowRight className="w-[18px] h-[18px]" /></Link>
              <a href="#testimonials-contact" className="inline-flex items-center gap-2 min-h-[50px] px-6 rounded-full border border-white/25 hover:border-[#FFC72C] hover:text-[#FFC72C] font-extrabold transition-colors" data-testid="testimonials-final-advisor">Talk to an Advisor</a>
            </div>
          </div>
          <div className="relative min-h-[220px] lg:min-h-0">
            <img src={CTA_IMG} alt="Laptop and notebook representing career preparation" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid="testimonials-final-image" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0F1115] via-[#0F1115]/40 to-transparent" aria-hidden="true" />
            <span className="absolute right-8 top-1/2 -translate-y-1/2 -rotate-3 text-3xl leading-tight text-[#FFC72C]" style={{ fontFamily: "'Caveat', cursive" }} data-testid="testimonials-final-script">Learn.<br />Grow.<br />Get Hired.</span>
          </div>
        </div>
      </section>
    </div>
  );
}
